import { appFetch } from "../../utils/appFetch";

export interface AppFetchResponse {
    data: any;
    status: number;
}

export type V2EventCatalogDto = {
    existing_events: string[];
    suggested_events: string[];
};

export type V2AutomationDto = {
    id: number;
    shop_id: number;
    name: string;
    description: string | null;

    active: 0 | 1;
    deleted: 0 | 1;

    created_at?: string;
    updated_at?: string;
};

export type V2AutomationListItemDto = {
    id: number;
    shop_id: number;
    name: string;
    description: string | null;
    active: 0 | 1;
    deleted: 0 | 1;
    created_at?: string;
    updated_at?: string;
    runs_count?: number;
    last_run_status?: string | null;
    last_run_entered_at?: string | null;
};

export type V2AutomationUpsertBody = {
    name: string;
    description: string | null;
    active: 0 | 1;
};

export type V2AutomationDraftSettingsDto = {
    id: number;
    shop_id: number;
    automation_id: number;
    name: string;
    description: string | null;
    active: 0 | 1;
    cooldown_seconds: number;
    max_active_runs_per_client: number;
    exit_condition_id: number | null;
    updated_at?: string;
};

export type V2TrackersDto = {
    events: Array<{
        event_name: string;
        required: boolean;
        description?: string;
        example_code?: string;
    }>;
    integration_snippet?: string;
};

export type V2TriggerDto = {
    id: number;
    shop_id: number;
    automation_id: number;
    title: string;
    entry_condition_id: number;
    exit_condition_id: number | null;
    cooldown_seconds: number;
    max_active_runs_per_client: number;
    active: 0 | 1;
    deleted: 0 | 1;
    created_at?: string;
    updated_at?: string;
    entry_event_basic_condition_id?: number | null;
    entry_event_name?: string | null;
};

export type V2AutomationStepDto = {
    step_index: number;
    step_type: "wait" | "send_whatsapp_template" | "condition" | "wait_event" | "action" | string;
    config: any;
};

export type V2AutomationEdgeDto = {
    from_step_index: number;
    to_step_index: number;
    edge_key: string;
    sort_order?: number;
};

export type V2TemplateListItemDto = {
    id: number;
    title: string;
    channel: string;
    status: string;
    category: string | null;
    // Server may return this as a JSON string or already-parsed array.
    variables_schema_json?: any;
    content_types_json?: any;
    provider_content_sid?: string | null;
};

export type V2TemplatesContextDto = {
    templates: V2TemplateListItemDto[];
};

export type V2RunListItemDto = {
    id: number;
    shop_id: number;
    client_id: number;
    trigger_id: number;
    trigger_title: string | null;
    status: "active" | "completed" | "cancelled" | "paused" | "error";
    current_step_index: number;
    next_step_due_at: string | null;
    entered_at: string;
    ended_at: string | null;
    cancel_reason: string | null;
    context_entity_type: string | null;
    context_entity_id: number | string | null;
};

export type V2RunLogItemDto = {
    id: number;
    run_id: number;
    ts: string;
    event_type: string;
    step_index: number | null;
    idempotency_key: string | null;
    payload_json: any;
    result_json: any;
};

function unwrap<T>(res: AppFetchResponse): { ok: boolean; data?: T; error?: string } {
    const ok = res.status >= 200 && res.status < 300;
    const payload = res?.data;

    if (payload && typeof payload === "object" && "data" in payload) {
        return { ok, data: (payload as any).data as T, error: (payload as any).error };
    }

    return { ok, data: payload as T, error: (payload as any)?.error };
}

export const GetV2EventCatalog = async (domain: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/events/catalog`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<V2EventCatalogDto>(res);
};

export const GetV2Automation = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<V2AutomationDto>(res);
};

export const ListV2Automations = async (
    domain: string,
    params?: { q?: string; status?: "all" | "active" | "inactive"; limit?: number; offset?: number }
) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.status) qs.set("status", params.status);
    if (typeof params?.limit === "number") qs.set("limit", String(params.limit));
    if (typeof params?.offset === "number") qs.set("offset", String(params.offset));
    const query = qs.toString();
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations${query ? `?${query}` : ""}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ automations: V2AutomationListItemDto[]; total: number }>(res);
};

export const CreateV2Automation = async (domain: string, body: V2AutomationUpsertBody) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });

    return unwrap<{ id: number }>(res);
};

export const UpdateV2Automation = async (
    domain: string,
    automationId: number,
    body: V2AutomationUpsertBody & { publish?: boolean }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });

    return unwrap<{ ok: true; published?: { ok: boolean; version_no?: number; already_published?: boolean } | null }>(res);
};

export const UpdateV2AutomationActive = async (
    domain: string,
    automationId: number,
    body: { active: 0 | 1 }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/active`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });

    return unwrap<{ ok: true }>(res);
};

export const DeleteV2Automation = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ ok: true }>(res);
};

export const GetV2AutomationDraftSettings = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/draft-settings`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<V2AutomationDraftSettingsDto | null>(res);
};

export const UpsertV2AutomationDraftSettings = async (
    domain: string,
    automationId: number,
    body: {
        name: string;
        description: string | null;
        active: 0 | 1;
        cooldown_seconds: number;
        max_active_runs_per_client: number;
        exit_condition_id: number | null;
    }
) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/draft-settings`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
        params: body,
    });
    return unwrap<V2AutomationDraftSettingsDto | null>(res);
};

export const EnsureV2AutomationDraftExitCondition = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/draft-exit-condition`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        domain,
    });
    return unwrap<{ exit_condition_id: number }>(res);
};

export const GetV2AutomationTrackers = async (domain: string, automationId: number) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/trackers`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<V2TrackersDto>(res);
};

export const GetV2AutomationSteps = async (
    domain: string,
    automationId: number,
    source?: "live" | "draft" | "published"
) => {
    const qs = source && source !== "live" ? `?source=${encodeURIComponent(source)}` : "";
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/steps${qs}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    const out = unwrap<any>(res);
    if (!out.ok) return out;

    const data = out.data;
    if (Array.isArray(data)) {
        return { ok: true, data: { steps: data as V2AutomationStepDto[], edges: [] as V2AutomationEdgeDto[] } };
    }

    const steps = Array.isArray(data?.steps) ? (data.steps as V2AutomationStepDto[]) : [];
    const edges = Array.isArray(data?.edges) ? (data.edges as V2AutomationEdgeDto[]) : [];
    return { ok: true, data: { steps, edges } };
};

export const ReplaceV2AutomationSteps = async (
    domain: string,
    automationId: number,
    steps: V2AutomationStepDto[],
    edges: V2AutomationEdgeDto[] = [],
    opts?: { publish?: boolean }
) => {
    const qs = opts?.publish ? "?publish=1" : "";
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/steps${qs}`,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        domain,
        params: { steps, edges },
    });

    return unwrap<{ ok: true; published?: { ok: boolean; version_no?: number; already_published?: boolean } | null }>(res);
};

// ✅ NEW: template context list (returns { templates: [...] })
export const GetV2TemplatesContextList = async (domain: string) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/ai/automation/context/templates`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<V2TemplatesContextDto>(res);
};

export const GetV2AutomationRuns = async (domain: string, automationId: number, limit = 50) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/automations/${automationId}/runs?limit=${encodeURIComponent(String(limit))}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ runs: V2RunListItemDto[] }>(res);
};

export const GetV2RunLogs = async (domain: string, runId: number, limit = 200) => {
    const res: AppFetchResponse = await appFetch({
        endpoint: `v2/runs/${runId}/logs?limit=${encodeURIComponent(String(limit))}`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        domain,
    });

    return unwrap<{ logs: V2RunLogItemDto[] }>(res);
};
